<script setup lang="ts">
import { onBeforeUnmount, ref, nextTick } from 'vue'
import { throttle } from 'lodash'
import { Portal } from 'portal-vue'
import { fl_followMouse } from '@/components/lib/GwUtils/utils_float'
import { addEventListeners, removeEventListeners } from '@/components/lib/GwUtils/utils_listeners'
import {
  type DragIdicatorStyleProps,
  type GwDragDropper,
} from '@/components/lib/GwDragDropper/GwDragDropperUtils'

const props = withDefaults(defineProps<GwDragDropper>(), {
  direction: 'vertical',
  getDragIdicatorStyle: (args: DragIdicatorStyleProps) => {
    const { height, width, left, right, top, bottom } = args.intersectionRect
    if (args.direction === 'horizontal') {
      return {
        display: 'block',
        top: `${top}px`,
        left: `${args.dropSurface === 'before' ? left : right}px`,
        width: `1px`,
        height: `${height}px`,
      }
    }
    return {
      display: 'block',
      top: `${args.dropSurface === 'before' ? top : bottom}px`,
      left: `${left}px`,
      width: `${width}px`,
      height: `1px`,
    }
  },
})

const emit = defineEmits(['drag:drop', 'drag:over'])

const shallowItemRef = ref()
const isDragging = ref()
const dropIndicatorStyle = ref({})
const draggedData = ref()
const dropSurface = ref('before')
let dropTarget: Element | null = null

const reset = () => {
  draggedData.value = null
  dropIndicatorStyle.value = {}
  isDragging.value = false
  dropSurface.value = 'before'
  dropTarget = null
}

const positionShallowEl = (e: Event) => {
  e.preventDefault()
  if (!shallowItemRef.value) return
  fl_followMouse(e, document.body, shallowItemRef.value, {
    offset: 8,
    padding: 8,
    strategy: 'fixed',
  })
}

const getDropTargetEl = (el: Element | null): Element | null => {
  if (!el) return null
  if (el.id === 'dropindicator') {
    return dropTarget
  }

  if (el.id.includes(props.dropTargetIdPart)) {
    return el
  }
  if (!el.parentElement) return null
  return getDropTargetEl(el.parentElement)
}

const dragOver = throttle(e => {
  e.preventDefault()
  positionShallowEl(e)

  if (!props.dropTargetIdPart) return

  let { clientX, clientY } = e
  if (e?.touches) {
    clientY = e?.touches[0]?.clientY
    clientX = e?.touches[0]?.clientX
  }

  const el = document.elementFromPoint(clientX, clientY)
  dropTarget = getDropTargetEl(el)
  if (!dropTarget) {
    dropIndicatorStyle.value = { display: 'none' }
    return
  }
  emit('drag:over', dropTarget)

  // intersectionRect tells us where the dropTarget is visible (not hidden by overflow)
  const observer = new IntersectionObserver(
    entries => {
      if (!dropTarget) return
      observer.unobserve(dropTarget as Element)
      const entry = entries[0]
      const { y, x, height, width } = entry.boundingClientRect
      if (props.direction === 'horizontal') {
        dropSurface.value = clientX >= x + width / 2 ? 'after' : 'before'
      } else if (props.direction === 'vertical') {
        dropSurface.value = clientY >= y + height / 2 ? 'after' : 'before'
      }
      dropIndicatorStyle.value = props.getDragIdicatorStyle({
        dropTarget: dropTarget as Element,
        intersectionRect: entry.intersectionRect,
        direction: props.direction,
        dropSurface: dropSurface.value,
      })
    },
    {
      rootMargin: '0px',
      threshold: 1,
    },
  )
  observer.observe(dropTarget)
}, 10)

const endDrag = (e: Event) => {
  if (dropTarget) {
    emit('drag:drop', e, {
      dropTarget,
      dropTargetIdPart: props.dropTargetIdPart,
      dropSurface: dropSurface.value,
      draggedData: draggedData.value,
    })
  }
  // eslint-disable-next-line no-use-before-define
  setupListeners(false)
  document.body.classList.remove('gw_dragging')
  reset()
}

const setupListeners = (open: boolean) => {
  const fn = open ? addEventListeners : removeEventListeners
  fn([
    [document, 'mousemove', dragOver],
    [document, 'mouseup', endDrag],
    [document, 'touchmove', dragOver],
    [document, 'touchend', endDrag],
  ])
}

const startDrag = (e: Event, data: any) => {
  e.preventDefault()
  document.body.classList.add('gw_dragging')
  isDragging.value = true

  draggedData.value = data
  setupListeners(true)
  nextTick(() => {
    dragOver(e)
  })
}

let delayedStartTimeout: ReturnType<typeof setTimeout>
const endDelayedStartDrag = () => {
  clearTimeout(delayedStartTimeout)
}
const startDelayedDrag = (e: Event, data: any) => {
  delayedStartTimeout = setTimeout(() => {
    startDrag(e, data)
  }, 333)
}

onBeforeUnmount(() => {
  endDelayedStartDrag()
  setupListeners(false)
})
</script>

<template>
  <portal
    v-if="isDragging"
    to="main_before"
  >
    <div class="gwdrgdrp text-noselect">
      <div
        ref="shallowItemRef"
        class="gwdrgdrp_shallowitem"
      >
        <slot
          name="shallowitem"
          :draggedData
        ></slot>
      </div>
      <div
        id="dropindicator"
        class="gwdrgdrp_dropindicator"
        :style="dropIndicatorStyle"
      >
        <slot name="dropindicator">
          <div
            style="height: inherit; width: inherit; background: var(--clr-primary); width: 100%"
          ></div>
        </slot>
      </div>
    </div>
  </portal>
  <slot
    :methods="{ startDrag, endDelayedStartDrag, startDelayedDrag }"
    :isDragging="isDragging"
  >
  </slot>
</template>

<style lang="scss" scoped>
.gwdrgdrp {
  position: fixed;
  z-index: 99;
  &_shallowitem {
    position: fixed;
    z-index: 99;
  }
  &_dropindicator {
    display: none;
    position: fixed;
    z-index: 99;
  }
}
</style>
